import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, RefreshCw, LayoutGrid, List, Clock, BarChart3, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/cn';
import { useStore } from '../store/useStore';
import { useLeadSettings } from '../store/useSettingsStore';
import { leadService } from '../lib/leadService';
import { Lead, LeadColor, LeadStage, LEAD_SOURCE_LABELS, LEAD_SOURCE_KEYS, leadStaleness } from '../types/leads.types';
import { LeadKanban } from '../components/leads/LeadKanban';
import { LeadCard } from '../components/leads/LeadCard';
import { LeadFormDialog } from '../components/leads/LeadFormDialog';
import { LostReasonDialog } from '../components/leads/LostReasonDialog';
import { LinkModuloCorsiDialog } from '../components/leads/LinkModuloCorsiDialog';

type ViewMode = 'kanban' | 'list';

export default function LeadsPage() {
    const navigate = useNavigate();
    const { addNotification, resetNewLeadCount } = useStore();
    const { giorniRicontatto } = useLeadSettings();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<ViewMode>('kanban');
    const [search, setSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState<string>('all');
    const [recontactOnly, setRecontactOnly] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingLead, setEditingLead] = useState<Lead | null>(null);
    const [dueReminderLeadIds, setDueReminderLeadIds] = useState<Set<string>>(new Set());
    const [lostFor, setLostFor] = useState<{ lead: Lead; prev: LeadStage } | null>(null);
    const [lostBusy, setLostBusy] = useState(false);
    const [isLinkCorsiOpen, setIsLinkCorsiOpen] = useState(false);

    const loadLeads = async () => {
        setLoading(true);
        try {
            const data = await leadService.getLeads();
            setLeads(data);
        } catch (error) {
            console.error('Error loading leads:', error);
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare i lead' });
        } finally {
            setLoading(false);
        }
    };

    const loadDueReminders = async () => {
        try {
            // Infinity: qui il badge "da ricontattare" deve mostrare anche gli arretrati,
            // a differenza delle notifiche che si fermano alle ultime ore.
            const due = await leadService.getDueReminders(0, Infinity);
            const ids = new Set<string>();
            due.forEach((r) => { if (r.event?.lead_id) ids.add(r.event.lead_id); });
            setDueReminderLeadIds(ids);
        } catch {
            /* non bloccante */
        }
    };

    useEffect(() => {
        loadLeads();
        loadDueReminders();
        resetNewLeadCount(); // aprendo la pagina Lead, azzera il pallino "nuovi"
    }, []);

    // lead che passano ricerca + fonte (prima del filtro "da ricontattare")
    const baseFiltered = useMemo(() => {
        return leads.filter((l) => {
            if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
            if (search) {
                const term = search.toLowerCase();
                const hit = [l.full_name, l.company_name, l.email, l.phone]
                    .filter(Boolean)
                    .some((v) => (v as string).toLowerCase().includes(term));
                if (!hit) return false;
            }
            return true;
        });
    }, [leads, sourceFilter, search]);

    // lead attivi fermi oltre soglia (ambra o rossi) = "da ricontattare".
    // La soglia si imposta in Impostazioni > Lead.
    const isRecontact = useCallback(
        (l: Lead) => leadStaleness(l, giorniRicontatto).level !== 'fresh',
        [giorniRicontatto]
    );
    const recontactCount = useMemo(() => baseFiltered.filter(isRecontact).length, [baseFiltered, isRecontact]);

    const filtered = useMemo(
        () => (recontactOnly ? baseFiltered.filter(isRecontact) : baseFiltered),
        [baseFiltered, recontactOnly, isRecontact]
    );

    // aggiorna stage con update ottimistico + patch opzionale (es. lost_reason)
    const applyStage = async (
        lead: Lead,
        newStage: LeadStage,
        patch: Partial<Pick<Lead, 'lost_reason' | 'next_action' | 'next_action_at'>> = {}
    ) => {
        const prev = lead.stage;
        setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, stage: newStage, ...patch } : l)));
        try {
            await leadService.updateStage(lead.id, newStage, prev, patch);
        } catch (error) {
            console.error('Error updating stage:', error);
            setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, stage: prev } : l)));
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare lo stato' });
        }
    };

    const handleStageChange = async (lead: Lead, newStage: LeadStage) => {
        // passaggio a "Perso": chiedi prima il motivo
        if (newStage === 'perso') {
            setLostFor({ lead, prev: lead.stage });
            return;
        }
        // uscendo da "Perso" azzera il motivo
        const patch = lead.stage === 'perso' ? { lost_reason: null } : {};
        await applyStage(lead, newStage, patch);
    };

    const confirmLost = async (reason: string) => {
        if (!lostFor) return;
        setLostBusy(true);
        await applyStage(lostFor.lead, 'perso', { lost_reason: reason });
        setLostBusy(false);
        setLostFor(null);
    };

    // colore card: update ottimistico + rollback in caso di errore
    const handleColorChange = async (lead: Lead, color: LeadColor | null) => {
        const prev = lead.color ?? null;
        setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, color } : l)));
        try {
            await leadService.setColor(lead.id, color);
        } catch (error) {
            console.error('Error updating lead color:', error);
            setLeads((cur) => cur.map((l) => (l.id === lead.id ? { ...l, color: prev } : l)));
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare il colore' });
        }
    };

    const reloadAll = () => { loadLeads(); loadDueReminders(); };

    const openNew = () => { setEditingLead(null); setIsFormOpen(true); };

    const countByStage = (stage: LeadStage) => filtered.filter((l) => l.stage === stage).length;

    return (
        <div className="space-y-4 md:space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Lead</h1>
                    <p className="text-sm text-gray-500">
                        {filtered.length} lead · {countByStage('vinto')} vinti · {countByStage('perso')} persi
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="flex rounded-lg border overflow-hidden w-full sm:w-auto">
                        <button
                            onClick={() => setView('kanban')}
                            className={cn('flex-1 sm:flex-none justify-center px-3 py-2 flex items-center gap-1.5 text-sm', view === 'kanban' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600')}
                        >
                            <LayoutGrid className="w-4 h-4" /> Pipeline
                        </button>
                        <button
                            onClick={() => setView('list')}
                            className={cn('flex-1 sm:flex-none justify-center px-3 py-2 flex items-center gap-1.5 text-sm', view === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600')}
                        >
                            <List className="w-4 h-4" /> Lista
                        </button>
                    </div>
                    <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => navigate('/leads/stats')}>
                        <BarChart3 className="w-4 h-4 mr-2" /> Statistiche
                    </Button>
                    <Button variant="outline" className="flex-1 sm:flex-none" onClick={reloadAll}>
                        <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
                    </Button>
                    <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => setIsLinkCorsiOpen(true)}>
                        <GraduationCap className="w-4 h-4 mr-2" /> Modulo corsi
                    </Button>
                    <Button onClick={openNew} className="flex-1 sm:flex-none">
                        <Plus className="w-4 h-4 mr-2" /> Nuovo Lead
                    </Button>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Cerca per nome, azienda, email, telefono..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Fonte" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutte le fonti</SelectItem>
                        {LEAD_SOURCE_KEYS.map((s) => (
                            <SelectItem key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <button
                    onClick={() => setRecontactOnly((v) => !v)}
                    title="Lead attivi fermi da troppo tempo, senza contatto recente"
                    className={cn(
                        'flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                        recontactOnly
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-gray-700 hover:bg-amber-50',
                        !recontactOnly && recontactCount > 0 && 'border-amber-300'
                    )}
                >
                    <Clock className="w-4 h-4" /> Da ricontattare
                    {recontactCount > 0 && (
                        <span className={cn(
                            'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                            recontactOnly ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-700'
                        )}>
                            {recontactCount}
                        </span>
                    )}
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Caricamento lead...</div>
            ) : view === 'kanban' ? (
                <LeadKanban
                    leads={filtered}
                    dueReminderLeadIds={dueReminderLeadIds}
                    onCardClick={(lead) => navigate(`/leads/${lead.id}`)}
                    onStageChange={handleStageChange}
                    onQuickLogged={reloadAll}
                    onColorChange={handleColorChange}
                />
            ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                    Nessun lead. Creane uno con "Nuovo Lead" o attendi gli import automatici.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filtered.map((lead) => (
                        <LeadCard
                            key={lead.id}
                            lead={lead}
                            hasDueReminder={dueReminderLeadIds.has(lead.id)}
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            onQuickLogged={reloadAll}
                            onColorChange={(color) => handleColorChange(lead, color)}
                        />
                    ))}
                </div>
            )}

            <LeadFormDialog
                open={isFormOpen}
                onOpenChange={setIsFormOpen}
                lead={editingLead}
                onSaved={loadLeads}
            />

            <LostReasonDialog
                open={!!lostFor}
                leadName={lostFor?.lead.full_name || lostFor?.lead.company_name || undefined}
                busy={lostBusy}
                onCancel={() => setLostFor(null)}
                onConfirm={confirmLost}
            />

            <LinkModuloCorsiDialog open={isLinkCorsiOpen} onOpenChange={setIsLinkCorsiOpen} />
        </div>
    );
}
